import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Document,
  Page,
  Text,
  View,
  pdf,
  StyleSheet,
  PDFDownloadLink,
  PDFViewer,
} from "@react-pdf/renderer";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/airbnb.css";
import * as Icons from "react-bootstrap-icons";
import { Tooltip } from "react-tooltip";

import { getProfitReport } from "../../../fetch/reportFetch";
import "./style.css";
// Fungsi format mata uang
const formatCurrency = (number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};
const tanggalWaktuIndonesia = new Date().toLocaleString("id-ID", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short", // Menampilkan nama zona waktu
});
const ProfitReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const flatpickrRef = useRef();
  const currentDate = new Date(); // Tanggal saat ini
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1); // Tanggal 1 Januari tahun ini
  const [dateRange, setDateRange] = useState([startOfYear, currentDate]);
  const [zoomLevel, setZoomLevel] = useState(1); // State for zoom level

  // Function to increase zoom level
  const zoomIn = () => {
    setZoomLevel((prevZoomLevel) =>
      prevZoomLevel < 2 ? prevZoomLevel + 0.1 : prevZoomLevel
    );
  };

  // Function to decrease zoom level
  const zoomOut = () => {
    setZoomLevel((prevZoomLevel) =>
      prevZoomLevel > 1 ? prevZoomLevel - 0.1 : prevZoomLevel
    );
  };
  useEffect(() => {
    fetchReportData(dateRange); // Pastikan fungsi ini dipanggil
  }, []);
  const fetchReportData = async (date) => {
    setLoading(true);
    try {
      getProfitReport({ rangeDate: date }, (response) => {
        setReportData(response);
        setLoading(false); // Pindahkan setLoading ke sini setelah data di-set
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
      setLoading(false); // Pastikan untuk menghentikan loading bila terjadi error
    }
  };
  const TableRow = ({
    leftText,
    rightText,
    isTotal,
    isHeader,
    isTitle,
    isLaba,
    isSubtitle,
    isSubRow,
    isSubTotal,
  }) => {
    let rowStyle, leftCellStyle, rightCellStyle;

    if (isHeader) {
      rowStyle = styles.tableHeader;
      leftCellStyle = styles.tableHeaderCellLeft;
      rightCellStyle = styles.tableHeaderCellRight;
    } else if (isTitle) {
      rowStyle = styles.tableRowTitle;
      leftCellStyle = styles.tableRowTitleCellLeft;
      rightCellStyle = styles.tableRowTitleCellRight;
    } else if (isSubtitle) {
      rowStyle = styles.tableRowSubTitle;
      leftCellStyle = styles.tableRowSubTitleCellLeft;
      rightCellStyle = styles.tableRowSubTitleCellRight;
    } else if (isLaba) {
      rowStyle = styles.tableRowLaba;
      leftCellStyle = styles.tableRowLabaCellLeft;
      rightCellStyle = styles.tableRowLabaCellRight;
    } else if (isSubRow) {
      rowStyle = styles.tableSubRow;
      leftCellStyle = styles.tableSubRowCellLeft;
      rightCellStyle = styles.tableSubRowCellRight;
    } else if (isSubTotal) {
      rowStyle = styles.tableRowSubTotal;
      leftCellStyle = styles.tableRowSubTotalCellLeft;
      rightCellStyle = styles.tableRowSubTotalCellRight;
    } else {
      rowStyle = isTotal ? styles.tableRowTotal : styles.tableRow;
      leftCellStyle = isTotal
        ? styles.tableRowTotalCellLeft
        : styles.tableCellLeft;
      rightCellStyle = isTotal
        ? styles.tableRowTotalCellRight
        : styles.tableCellRight;
    }

    return (
      <View style={rowStyle}>
        <Text style={leftCellStyle}>{leftText}</Text>
        <Text style={rightCellStyle}>{rightText}</Text>
      </View>
    );
  };

  // Definisikan gaya untuk PDF
  const styles = StyleSheet.create({
    page: {
      flexDirection: "column",
      padding: 50, // Sesuaikan padding sesuai dengan kebutuhan desain
      backgroundColor: "#FFF",
    },
    header: {
      margin: 10,
    },
    title: {
      fontSize: 18,
      textAlign: "center",
      color: "#012970",
      // Menggunakan Helvetica sebagai font bawaan
      fontFamily: "Helvetica-Bold",
    },
    company: {
      fontSize: 14,
      textAlign: "center",
      // Menggunakan Times-Roman sebagai font bawaan
      fontFamily: "Courier",
    },
    year: {
      fontSize: 12,
      textAlign: "center",
      fontWeight: "light",
      // Menggunakan Courier sebagai font bawaan
      fontFamily: "Courier",
    },
    table: {
      display: "table",
      width: "100%", // Lebar tabel menjadi 100%
      borderStyle: "solid",
    },
    tableHeader: {
      marginBottom: "10",
      flexDirection: "row",
      color: "#012970", // Warna teks untuk header
      borderBottomColor: "#012970",
      borderBottomWidth: 1,
      alignItems: "center",
      height: 24,
      fontSize: 10,
      fontWeight: "bold",
    },
    tableHeaderCellLeft: {
      width: "85%",
      textAlign: "left",
    },
    tableHeaderCellRight: {
      width: "15%",
      paddingRight: 8,
      textAlign: "right",
    },
    tableRowTitle: {
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "Helvetica-Bold",
      fontSize: 8, // Ukuran font untuk baris tabel
      fontWeight: "bold",
      paddingBottom: 4,
    },
    tableRowTitleCellRight: {
      width: "30%", // Lebar sel kanan disesuaikan
      textAlign: "right",
    },
    tableRowTitleCellLeft: {
      width: "70%", // Lebar sel kiri
      textAlign: "left",
    },
    tableRowSubTitle: {
      marginTop: 10,
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "Helvetica-Bold",
      fontSize: 8, // Ukuran font untuk baris tabel
      fontWeight: "bold",
      paddingBottom: 4,
      paddingLeft: 6,
    },
    tableRowSubTitleCellRight: {
      width: "30%", // Lebar sel kanan disesuaikan
      textAlign: "right",
    },
    tableRowSubTitleCellLeft: {
      width: "70%", // Lebar sel kiri
      textAlign: "left",
    },
    tableRowTotal: {
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "Helvetica-Bold",
      marginBottom: 10,
      fontSize: 8, // Ukuran font untuk baris tabel
      fontWeight: "bold",
      paddingTop: 4,
    },
    tableRowTotalCellRight: {
      width: "30%", // Lebar sel kanan disesuaikan
      textAlign: "right",
      paddingTop: 3,
      borderTopWidth: 1,
    },
    tableRowTotalCellLeft: {
      width: "70%", // Lebar sel kiri
      textAlign: "left",
    },
    tableRowSubTotal: {
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "Helvetica-Bold",
      marginBottom: 10,
      fontSize: 8, // Ukuran font untuk baris tabel
      fontWeight: "bold",
      paddingTop: 4,
      paddingLeft: 6,
    },
    tableRowSubTotalCellRight: {
      width: "30%", // Lebar sel kanan disesuaikan
      textAlign: "right",
      paddingTop: 3,
      borderTopWidth: 1,
    },
    tableRowSubTotalCellLeft: {
      width: "70%", // Lebar sel kiri
      textAlign: "left",
    },
    tableRowLaba: {
      flexDirection: "row",
      alignItems: "center",
      fontFamily: "Helvetica-Bold",
      marginBottom: 20,
      marginTop: 10,
      fontSize: 8, // Ukuran font untuk baris tabel
      fontWeight: "bold",
    },
    tableRowLabaCellLeft: {
      width: "70%", // Lebar sel kiri
      textAlign: "left",
    },
    tableRowLabaCellRight: {
      width: "30%", // Lebar sel kanan disesuaikan
      textAlign: "right",
      borderTopWidth: 1,
      paddingTop: 3,
    },
    tableSubRow: {
      flexDirection: "row",
      fontFamily: "Helvetica",
      fontWeight: "thin",
      alignItems: "center",
      fontSize: 8, // Ukuran font untuk baris tabel
      paddingLeft: 6,
    },
    tableSubRowCellLeft: {
      width: "60%", // Lebar sel kiri
      textAlign: "left",
      padding: 8,
    },
    tableSubRowCellRight: {
      width: "40%", // Lebar sel kanan disesuaikan
      paddingBottom: 4,
      textAlign: "right",
    },
    tableRow: {
      flexDirection: "row",
      fontFamily: "Helvetica",
      fontWeight: "thin",
      alignItems: "center",
      fontSize: 8, // Ukuran font untuk baris tabel
    },
    tableCellLeft: {
      width: "60%", // Lebar sel kiri
      textAlign: "left",
      padding: 8,
    },
    tableCellRight: {
      width: "40%", // Lebar sel kanan disesuaikan
      paddingBottom: 4,
      textAlign: "right",
    },
    footer: {
      textAlign: "center",
      marginTop: "auto",
      borderTopColor: "#bfbfbf",
      borderTop: 1,
      fontFamily: "Courier",
      whiteSpace: "pre-wrap",
      fontSize: 10, // Sesuaikan ukuran font footer
    },
  });

  // Fungsi untuk membuat konten PDF
  const ProfitReportPDF = ({ reportData }) => {
    const { Company, Year, Report } = reportData;
    let totalPendapatan = 0;
    let totalPendapatanLainnya = 0;
    let totalBPP = 0;
    let totalBebanOperasional = 0;
    let totalBebanLainnya = 0;

    if (Report) {
      if (Report.Pendapatan) {
        totalPendapatan = Object.values(Report.Pendapatan).reduce(
          (sum, current) => sum + parseFloat(current || 0),
          0
        );
      }

      if (Report.BebanPokokPenjualan) {
        totalBPP = Object.values(Report.BebanPokokPenjualan).reduce(
          (sum, current) => sum + parseFloat(current || 0),
          0
        );
      }

      if (Report.BebanOperasional) {
        totalBebanOperasional = Object.values(Report.BebanOperasional).reduce(
          (sum, current) => sum + parseFloat(current || 0),
          0
        );
      }
      if (Report.PendapatanLainnya) {
        totalPendapatanLainnya = Object.values(Report.PendapatanLainnya).reduce(
          (sum, current) => sum + parseFloat(current || 0),
          0
        );
      }
      if (Report.BebanLainnya) {
        totalBebanLainnya = Object.values(Report.BebanLainnya).reduce(
          (sum, current) => sum + parseFloat(current || 0),
          0
        );
      }
    }

    const pendapataOperasional = totalPendapatan - totalBebanOperasional;
    const jumlahNonOperasional = totalPendapatanLainnya - totalBebanLainnya;
    const labaKotor = totalPendapatan - totalBPP;
    return (
      <Document>
        <Page style={styles.page}>
          <View fixed style={styles.header}>
            {/* Judul, Nama Perusahaan, dan Tahun */}
            <Text style={styles.company}>{Company}</Text>
            <Text style={styles.title}>Laporan Laba Rugi</Text>
            <Text style={styles.year}>Dari {Year}</Text>
          </View>
          <View style={styles.table}>
            {/* Header Tabel */}
            <View fixed>
              <TableRow
                leftText="Deskripsi"
                rightText="Jumlah (IDR)"
                isHeader
              />
            </View>
            <TableRow leftText="PENDAPATAN" rightText="" isTitle />
            {/* Isi Tabel */}
            {Object.entries(Report.Pendapatan).map(([key, value]) => (
              <TableRow leftText={key} rightText={formatCurrency(value)} />
            ))}
            <TableRow
              leftText="Jumlah Pendapatan"
              rightText={formatCurrency(totalPendapatan)}
              isTotal
            />

            {/* Beban Pokok Penjualan */}
            <TableRow leftText="BEBAN POKOK PENJUALAN" rightText="" isTitle />

            {Object.entries(Report.BebanPokokPenjualan).map(([key, value]) => (
              <TableRow leftText={key} rightText={formatCurrency(value)} />
            ))}

            <TableRow
              leftText="Jumlah Beban Pokok Penjualan"
              rightText={formatCurrency(totalBPP)}
              isTotal
            />
            {/* Laba Kotor */}
            <TableRow
              leftText="LABA KOTOR"
              rightText={formatCurrency(labaKotor)}
              isLaba
            />
            {/* Beban Operasional */}
            <TableRow leftText="BEBAN OPERASIONAL " rightText="" isTitle />

            {Object.entries(Report.BebanOperasional).map(([key, value]) => (
              <TableRow leftText={key} rightText={formatCurrency(value)} />
            ))}
            <TableRow
              leftText="Jumlah Beban Operasional"
              rightText={formatCurrency(totalBebanOperasional)}
              isTotal
            />
            <TableRow
              leftText="PENDAPATAN OPERASIONAL"
              rightText={formatCurrency(pendapataOperasional)}
              isLaba
            />
            {/* PENDAPATAN DAN BEBAN NON OPERASIONAL	 */}
            <TableRow
              leftText="PENDAPATAN DAN BEBAN NON OPERASIONAL	 "
              rightText=""
              isTitle
            />
            {/* PENDAPATAN NON OPERASIONAL	 */}
            <TableRow
              leftText="Pendapatan Non Operasional"
              rightText=""
              isSubtitle
            />
            {/* Isi Tabel */}
            {Object.entries(Report.PendapatanLainnya).map(([key, value]) => (
              <TableRow
                leftText={key}
                rightText={formatCurrency(value)}
                isSubRow
              />
            ))}
            <TableRow
              leftText="Jumlah Pendapatan Non Operasional"
              rightText={formatCurrency(totalPendapatanLainnya)}
              isSubTotal
            />
            {/* Beban NON OPERASIONAL	 */}
            <TableRow leftText="Beban Non Operasional" rightText="" isSubtitle />
            {/* Isi Tabel */}
            {Object.entries(Report.BebanLainnya).map(([key, value]) => (
              <TableRow leftText={key} rightText={formatCurrency(value)}  isSubRow/>
            ))}
            <TableRow
              leftText="Jumlah Beban Non Operasional"
              rightText={formatCurrency(totalBebanLainnya)}
              isSubTotal
            />
            <TableRow
              leftText="Jumlah Pendapatan dan Beban Non Operasional"
              rightText={formatCurrency(jumlahNonOperasional)}
              isTotal
            />
            {/* Laba Bersih Sebelum Pajak */}
            <TableRow
              leftText="LABA BERSIH"
              rightText={formatCurrency(Report.LabaBersih)}
              isLaba
            />
          </View>
          {/* Footer */}
          <Text fixed style={styles.footer}>
            {"RanaBlue Accounting System Report\nTercetak pada " +
              tanggalWaktuIndonesia}
          </Text>
        </Page>
      </Document>
    );
  };

  const handleDateChange = (dates) => {
    // Memeriksa apakah pengguna sudah selesai memilih tanggal
    const [startDate, endDate] = dates;
    if (startDate && endDate) {
      fetchReportData(dates);
      setDateRange(dates);
    }
  };

  const refreshReport = () => {
    fetchReportData(dateRange);
  };

  // Fungsi untuk mencetak PDF
  const printPDF = () => {
    pdf(<ProfitReportPDF reportData={reportData} />)
      .toBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank"); // Buka PDF di jendela/tab baru
      })
      .catch((error) => {
        console.error("Error creating PDF blob:", error);
      });
  };

  return (
    <>
      {loading ? (
        <div className="spinner-main">
          <div className="loader">
            <div className="bounce1"></div>
            <div className="bounce2"></div>
            <div className="bounce3"></div>
          </div>
          <p>Tunggu Sebentar...</p>
        </div>
      ) : (
        <>
          <div className="header-page d-flex justify-content-between">
            <div className="pagetitle">
              <h1>Laporan Laba/Rugi</h1>
              <nav>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item">Laporan Keuangan</li>

                  <li className="breadcrumb-item active">Laba/Rugi</li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="custom-toolbar">
            <div className="card ">
              <div
                className="card-body d-flex align-center justify-content-center"
                style={{ gap: 20 }}
              >
                <div
                  className="flatpckr me-3"
                  data-tooltip-id="toollbarCalendar"
                >
                  <Flatpickr
                    ref={flatpickrRef}
                    value={dateRange}
                    onChange={handleDateChange}
                    options={{
                      mode: "range",
                      altInput: true,
                      altFormat: "F j, Y",
                      dateFormat: "Y-m-d",
                    }}
                  />
                  <Tooltip
                    id="toollbarCalendar"
                    place="top"
                    ariant="info"
                    content="Pilih Periode"
                  />
                </div>
                <Link onClick={refreshReport}>
                  <Icons.ArrowRepeat
                    data-tooltip-id="toollbarRefresh"
                    size={24}
                  />
                  <Tooltip
                    id="toollbarRefresh"
                    place="top"
                    variant="info"
                    content="Refresh"
                  />
                </Link>
                <PDFDownloadLink
                  document={<ProfitReportPDF reportData={reportData} />}
                  fileName={`laporan-laba-rugi-${reportData.Company}.pdf`}
                >
                  <Icons.Download
                    data-tooltip-id="toollbarDownload"
                    size={24}
                  />
                  <Tooltip
                    id="toollbarDownload"
                    place="top"
                    variant="info"
                    content="Download"
                  />
                </PDFDownloadLink>
                <Link onClick={printPDF}>
                  <Icons.Printer data-tooltip-id="toollbarPrint" size={24} />
                  <Tooltip
                    id="toollbarPrint"
                    place="top"
                    variant="info"
                    content="Print"
                  />
                </Link>
                <Link onClick={zoomIn}>
                  <Icons.ZoomIn data-tooltip-id="toollbarZoomIn" size={24} />
                  <Tooltip
                    id="toollbarZoomIn"
                    place="top"
                    variant="info"
                    content="Zoom-In"
                  />
                </Link>
                <Link onClick={zoomOut}>
                  <Icons.ZoomOut data-tooltip-id="toollbarZoomOut" size={24} />
                  <Tooltip
                    id="toollbarZoomOut"
                    place="top"
                    variant="info"
                    content="Zoom-Out"
                  />
                </Link>
              </div>
            </div>
          </div>
          {reportData && (
            <>
              <PDFViewer
                width={`${100 / zoomLevel}%`}
                height={`${420 / zoomLevel}vh`}
                showToolbar={false}
                style={{
                  overflow: "auto", // Memastikan kita bisa scroll jika konten melebihi batas viewport
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: "top left",
                  position: "relative", // Memastikan kontainer tetap pada posisi yang benar
                }}
              >
                <ProfitReportPDF reportData={reportData} />
              </PDFViewer>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ProfitReport;
